/** @format */

import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Chip,
    Stack,
    Typography,
    alpha,
} from "@mui/material";
import { keyframes } from "@mui/system";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import Groups2RoundedIcon from "@mui/icons-material/Groups2Rounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import { selectCurrentUser } from "../../redux/selectors/userSelector";
import { selectAllCircles } from "../../redux/selectors/circleSelector";
import { RoleNames } from "../../utility/types/rolesType";
import { colors } from "../../utility/types/constant";
import HowItWorksModal from "../how-it-works/HowItWorksModal";
import SendCircleInviteModal from "../modal/circle-invitation-modal/SendCircleInviteModal";

const CIRCLE_CREATE_LIMIT = 2;

const pulseRing = keyframes`
	0% { box-shadow: 0 0 0 0 ${alpha(colors.secondaryMain, 0.55)}; }
	70% { box-shadow: 0 0 0 10px ${alpha(colors.secondaryMain, 0)}; }
	100% { box-shadow: 0 0 0 0 ${alpha(colors.secondaryMain, 0)}; }
`;

type LeaderCircle = {
    _id?: string;
    circleName?: string;
    name?: string;
    members?: unknown[];
    leader?: { _id?: string } | string | null;
};

const getLeaderId = (circle: LeaderCircle) =>
    typeof circle?.leader === "object" ? circle?.leader?._id : circle?.leader;

const getCircleName = (circle?: LeaderCircle) =>
    circle?.circleName ?? circle?.name ?? "";

/**
 * Turns the hidden "Be a Circle Leader" drawer link into a visible, state-aware
 * path on the dashboard: apply to lead → create a circle → invite members.
 */
const CircleLaunchpad: React.FC = () => {
    const navigate = useNavigate();
    const userData = useSelector(selectCurrentUser);
    const allCircles = useSelector(selectAllCircles);

    const [howItWorksOpen, setHowItWorksOpen] = useState(false);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [selectedCircleId, setSelectedCircleId] = useState<string | null>(null);

    // Same roles the /circle-create route and checkAdminOrUnionLeader allow.
    const isLeader =
        userData?.roles?.some((r) =>
            [RoleNames.UNION_LEADER, RoleNames.ADMIN, RoleNames.SUPER_ADMIN].includes(
                r.roleName as RoleNames,
            ),
        ) ?? false;

    const leaderCircles: LeaderCircle[] = useMemo(() => {
        const source = Array.isArray(allCircles) ? (allCircles as LeaderCircle[]) : [];
        const fromStore = source.filter((circle) => {
            const leaderId = getLeaderId(circle);
            return Boolean(leaderId) && leaderId === userData?._id;
        });

        if (fromStore.length > 0) return fromStore;

        // circleInfo is shared state and can hold member circles instead; fall back
        // to the circles already attached to the user record.
        const fromUser = (userData?.unionLeaderOf ?? []) as LeaderCircle[];
        return fromUser.filter((circle) => Boolean(circle?._id));
    }, [allCircles, userData?._id, userData?.unionLeaderOf]);

    const circleCount = leaderCircles.length;
    const hasInvitedOthers = leaderCircles.some(
        (circle) => (circle.members?.length ?? 0) > 1,
    );

    const steps = [
        { label: "Become a circle leader", done: isLeader },
        { label: "Create your circle", done: circleCount > 0 },
        { label: "Invite your members", done: hasInvitedOthers },
    ];

    const activeStep = !isLeader ? 0 : circleCount === 0 ? 1 : 2;

    const activeCircle =
        leaderCircles.find((circle) => circle._id === selectedCircleId) ??
        leaderCircles[0];

    const copy = [
        {
            title: "Start your own savings circle",
            body: "Leading a circle lets you set the contribution amount, the payout schedule, and exactly who's in it. It takes three steps, here's the first.",
            cta: "Apply to lead a circle",
            onCta: () => navigate("/applications/leader"),
        },
        {
            title: "You're approved to lead",
            body: "Set up your circle next: name it, pick the contribution amount and schedule, then invite the people you trust.",
            cta: "Create your circle",
            onCta: () => navigate("/circle-create"),
        },
        {
            title: getCircleName(activeCircle)
                ? `Invite members to ${getCircleName(activeCircle)}`
                : "Invite your members",
            body: "A circle only starts paying out once your members join. Send an invite and they'll get an email with everything they need.",
            cta: "Invite members",
            onCta: () => setInviteOpen(true),
        },
    ][activeStep];

    const canCreateAnother = isLeader && circleCount > 0 && circleCount < CIRCLE_CREATE_LIMIT;

    return (
        <Box
            sx={{
                position: "relative",
                overflow: "hidden",
                borderRadius: "20px",
                border: `1px solid ${alpha(colors.primary, 0.08)}`,
                background: `linear-gradient(135deg, #ffffff 0%, ${alpha(colors.mainSecondary, 0.05)} 100%)`,
                boxShadow: `0 10px 34px ${alpha(colors.primary, 0.08)}`,
                p: { xs: 2.5, sm: 3.5 },
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "4px",
                    background: `linear-gradient(90deg, ${colors.mainSecondary} 0%, ${colors.secondaryMain} 100%)`,
                },
            }}>
            <Groups2RoundedIcon
                sx={{
                    position: "absolute",
                    top: -24,
                    right: -16,
                    fontSize: 170,
                    opacity: 0.045,
                    color: colors.primary,
                    pointerEvents: "none",
                }}
            />

            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={{ xs: 3, md: 4 }}
                alignItems={{ xs: "stretch", md: "center" }}
                sx={{ position: "relative" }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Chip
                        size="small"
                        label={`Step ${activeStep + 1} of 3`}
                        sx={{
                            mb: 1.5,
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 700,
                            fontSize: "0.68rem",
                            letterSpacing: "0.04em",
                            color: colors.mainSecondary,
                            bgcolor: alpha(colors.mainSecondary, 0.12),
                            border: `1px solid ${alpha(colors.mainSecondary, 0.3)}`,
                        }}
                    />

                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 800,
                            fontSize: { xs: "1.2rem", sm: "1.45rem" },
                            color: colors.primary,
                            lineHeight: 1.25,
                            letterSpacing: "-0.01em",
                        }}>
                        {copy.title}
                    </Typography>

                    <Typography
                        sx={{
                            mt: 1,
                            maxWidth: 520,
                            fontSize: "0.92rem",
                            lineHeight: 1.7,
                            color: "#5b6472",
                        }}>
                        {copy.body}
                    </Typography>

                    {activeStep === 2 && leaderCircles.length > 1 && (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                            {leaderCircles.map((circle) => {
                                const selected = circle._id === activeCircle?._id;
                                return (
                                    <Chip
                                        key={circle._id}
                                        label={getCircleName(circle) || "Circle"}
                                        onClick={() => setSelectedCircleId(circle._id ?? null)}
                                        sx={{
                                            fontWeight: 600,
                                            color: selected ? colors.white : colors.primary,
                                            bgcolor: selected
                                                ? colors.mainSecondary
                                                : alpha(colors.primary, 0.06),
                                            "&:hover": {
                                                bgcolor: selected
                                                    ? colors.mainSecondary
                                                    : alpha(colors.primary, 0.12),
                                            },
                                        }}
                                    />
                                );
                            })}
                        </Stack>
                    )}

                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                        sx={{ mt: 2.5 }}
                        alignItems={{ xs: "stretch", sm: "center" }}>
                        <Button
                            variant="contained"
                            disableElevation
                            onClick={copy.onCta}
                            startIcon={activeStep === 2 ? <PersonAddAlt1RoundedIcon /> : undefined}
                            endIcon={activeStep === 2 ? undefined : <ArrowForwardRoundedIcon />}
                            sx={{
                                px: 3,
                                py: 1.25,
                                borderRadius: "12px",
                                textTransform: "none",
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 700,
                                fontSize: "0.95rem",
                                background: `linear-gradient(135deg, ${colors.mainSecondary} 0%, #17a37d 100%)`,
                                boxShadow: `0 8px 22px ${alpha(colors.mainSecondary, 0.35)}`,
                                "&:hover": {
                                    background: `linear-gradient(135deg, #25d6a6 0%, ${colors.mainSecondary} 100%)`,
                                    boxShadow: `0 12px 28px ${alpha(colors.mainSecondary, 0.45)}`,
                                },
                            }}>
                            {copy.cta}
                        </Button>

                        {activeStep === 2 && activeCircle?._id && (
                            <Button
                                variant="outlined"
                                onClick={() => navigate(`/circle/${activeCircle._id}`)}
                                sx={{
                                    px: 2.5,
                                    py: 1.25,
                                    borderRadius: "12px",
                                    textTransform: "none",
                                    fontWeight: 600,
                                    color: colors.primary,
                                    borderColor: alpha(colors.primary, 0.25),
                                    "&:hover": { borderColor: colors.primary },
                                }}>
                                Manage circle
                            </Button>
                        )}

                        {canCreateAnother && (
                            <Button
                                variant="outlined"
                                onClick={() => navigate("/circle-create")}
                                sx={{
                                    px: 2.5,
                                    py: 1.25,
                                    borderRadius: "12px",
                                    textTransform: "none",
                                    fontWeight: 600,
                                    color: colors.primary,
                                    borderColor: alpha(colors.primary, 0.25),
                                    "&:hover": { borderColor: colors.primary },
                                }}>
                                Create another circle
                            </Button>
                        )}

                        <Button
                            variant="text"
                            startIcon={<HelpOutlineRoundedIcon />}
                            onClick={() => setHowItWorksOpen(true)}
                            sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                color: "#5b6472",
                                "&:hover": { color: colors.mainSecondary, bgcolor: "transparent" },
                            }}>
                            How it works
                        </Button>
                    </Stack>
                </Box>

                <Stack
                    spacing={0}
                    sx={{
                        minWidth: { md: 230 },
                        pl: { md: 3 },
                        borderLeft: { md: `1px dashed ${alpha(colors.primary, 0.14)}` },
                        borderTop: { xs: `1px dashed ${alpha(colors.primary, 0.14)}`, md: "none" },
                        pt: { xs: 2.5, md: 0 },
                    }}>
                    {steps.map((step, index) => {
                        const isActive = index === activeStep && !step.done;
                        const isLast = index === steps.length - 1;
                        return (
                            <Stack key={step.label} direction="row" spacing={1.5}>
                                <Stack alignItems="center" sx={{ width: 28 }}>
                                    <Box
                                        sx={{
                                            width: 26,
                                            height: 26,
                                            borderRadius: "50%",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexShrink: 0,
                                            fontFamily: "'Montserrat', sans-serif",
                                            fontSize: "0.75rem",
                                            fontWeight: 700,
                                            color: step.done
                                                ? colors.white
                                                : isActive
                                                    ? colors.primary
                                                    : "#9aa3b2",
                                            bgcolor: step.done
                                                ? colors.mainSecondary
                                                : isActive
                                                    ? colors.secondaryMain
                                                    : alpha(colors.primary, 0.07),
                                            animation: isActive ? `${pulseRing} 2.2s ease-out infinite` : "none",
                                            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                                        }}>
                                        {step.done ? <CheckRoundedIcon sx={{ fontSize: 16 }} /> : index + 1}
                                    </Box>
                                    {!isLast && (
                                        <Box
                                            sx={{
                                                width: "2px",
                                                flex: 1,
                                                minHeight: 22,
                                                my: 0.5,
                                                borderRadius: 1,
                                                bgcolor: steps[index + 1].done || step.done
                                                    ? alpha(colors.mainSecondary, 0.45)
                                                    : alpha(colors.primary, 0.1),
                                            }}
                                        />
                                    )}
                                </Stack>

                                <Typography
                                    sx={{
                                        pb: isLast ? 0 : 1.5,
                                        fontSize: "0.86rem",
                                        fontWeight: isActive ? 700 : 500,
                                        color: step.done
                                            ? colors.primary
                                            : isActive
                                                ? colors.primary
                                                : "#9aa3b2",
                                    }}>
                                    {step.label}
                                </Typography>
                            </Stack>
                        );
                    })}
                </Stack>
            </Stack>

            <HowItWorksModal open={howItWorksOpen} onOpenChange={setHowItWorksOpen} />

            {inviteOpen && activeCircle?._id && (
                <SendCircleInviteModal
                    open={inviteOpen}
                    handleClose={() => setInviteOpen(false)}
                    circleId={activeCircle._id}
                    circleName={getCircleName(activeCircle)}
                />
            )}
        </Box>
    );
};

export default CircleLaunchpad;
